import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Camera, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useCreateMaintenanceReport } from "@/hooks/useMaintenanceReports";
import type { ProofStandard } from "@/hooks/useProofStandards";
import { sendMaintenanceIssuesEmail } from "@/lib/notificationEmails";

interface UnitCheckIssueReportProps {
  standard: ProofStandard;
  propertyId: string;
  cleaningTaskId: string;
  cleanerId: string;
  checklistName?: string | null;
  /** Active walkthrough room (overrides standard.room for multi-room checks). */
  roomNameOverride?: string | null;
  tDynamic: (s: string) => string;
  disabled?: boolean;
}

export function UnitCheckIssueReport({
  standard,
  propertyId,
  cleaningTaskId,
  cleanerId,
  checklistName,
  roomNameOverride = null,
  tDynamic,
  disabled = false,
}: UnitCheckIssueReportProps) {
  const roomName =
    roomNameOverride?.trim() ||
    standard.rooms?.[0]?.trim() ||
    standard.room?.trim() ||
    tDynamic("General");
  const createReport = useCreateMaintenanceReport();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!notes.trim()) {
      toast({
        title: tDynamic("Describe the issue"),
        description: tDynamic("Add a short note so the owner knows what to fix."),
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const photoUrls: string[] = [];
      if (photoFile) {
        const ext = photoFile.name.split(".").pop()?.toLowerCase() || "jpg";
        const safeTitle = standard.title.replace(/[^a-zA-Z0-9]/g, "_").slice(0, 40);
        const path = `${propertyId}/${cleaningTaskId}/unit-check-issues/${standard.id}_${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("cleaning-photos")
          .upload(path, photoFile, { contentType: photoFile.type || "image/jpeg", upsert: true });
        if (uploadError) throw uploadError;
        const { data: pub } = supabase.storage.from("cleaning-photos").getPublicUrl(path);
        if (pub?.publicUrl) photoUrls.push(pub.publicUrl);
      }

      await createReport.mutateAsync({
        property_id: propertyId,
        cleaning_task_id: cleaningTaskId,
        task_description: standard.title,
        room_name: roomName,
        checklist_name: checklistName ?? undefined,
        cleaner_id: cleanerId,
        response: "needs_repair",
        notes: notes.trim(),
        photos: photoUrls,
      });

      await sendMaintenanceIssuesEmail({
        propertyId,
        taskId: cleaningTaskId,
        issues: [
          {
            room_name: roomName,
            description: `${standard.title}: ${notes.trim()}`,
            photo_url: photoUrls[0] ?? null,
          },
        ],
      });

      toast({
        title: tDynamic("Issue reported"),
        description: tDynamic("The owner was notified about this unit check item."),
      });
      setOpen(false);
      setNotes("");
      setPhotoFile(null);
    } catch (err) {
      console.error("[UnitCheckIssueReport]", err);
      toast({
        title: tDynamic("Error"),
        description: err instanceof Error ? err.message : tDynamic("Failed to send report"),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 gap-1.5 px-2 text-amber-800 hover:bg-amber-50 hover:text-amber-900 dark:text-amber-200 dark:hover:bg-amber-950/40"
        disabled={disabled || submitting}
        onClick={() => setOpen((v) => !v)}
      >
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {open ? tDynamic("Cancel report") : tDynamic("Report issue")}
      </Button>
      {open ? (
        <div className="space-y-2 rounded-lg border border-amber-200/80 bg-amber-50/50 p-3 dark:border-amber-900/40 dark:bg-amber-950/20">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder={tDynamic("What's wrong with this item?")}
            className="min-h-[4.5rem] bg-background text-sm"
            disabled={submitting}
          />
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-dashed px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted/40">
              <Camera className="h-3.5 w-3.5" aria-hidden />
              {photoFile ? photoFile.name : tDynamic("Add photo (optional)")}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={submitting}
                onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <Button type="button" size="sm" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              {tDynamic("Send report")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
